"use client";

import { IconArrowLeftOutline18, IconArrowRightOutline18, IconSparkle3Outline18 } from "nucleo-ui-outline-18";
import { PageLayout } from "@/components/ui/page-layout";
import { Button } from "@/components/ui/button";
import { useX402ImportWizard, X402StepIndicator } from "@/components/x402/X402ImportWizard";
import { PixelLoader } from "@/components/ui/pixel-loader";
import Link from "next/link";

interface X402ImportPageClientProps {
  initialUrl: string;
}

export default function X402ImportPageClient({ initialUrl }: X402ImportPageClientProps) {
  const wizard = useX402ImportWizard({
    initialUrl,
    onImported: () => {
      // Stay on success page - user can click "Create Workflow" or "Import Another"
    },
    onImportAnother: () => {
      // Reset is handled internally
    },
    variant: "page",
  });

  const renderFooter = () => {
    switch (wizard.step) {
      case "input":
        return (
          <div className="flex items-center justify-end gap-3">
            <Link href="/integrations">
              <Button variant="white">
                <span className="text-xs">Cancel</span>
              </Button>
            </Link>
            <Button onClick={wizard.handleProbe} disabled={!wizard.canProbe}>
              <span className="flex items-center gap-2 text-xs">
                {wizard.isProbing ? (
                  <>
                    <PixelLoader size="xs" />
                    Probing...
                  </>
                ) : (
                  <>
                    Probe Endpoint
                    <IconArrowRightOutline18 className="h-3 w-3" />
                  </>
                )}
              </span>
            </Button>
          </div>
        );

      case "probe":
        return (
          <div className="flex items-center justify-end gap-3">
            <Button variant="white" onClick={wizard.handleBack} disabled={wizard.isImporting}>
              <span className="flex items-center gap-2 text-xs">
                <IconArrowLeftOutline18 className="h-3 w-3" />
                Back
              </span>
            </Button>
            <Button onClick={wizard.handleAtomicImport} disabled={!wizard.canImport}>
              <span className="flex items-center gap-2 text-xs">
                {wizard.isImporting ? (
                  <>
                    <PixelLoader size="xs" />
                    Importing...
                  </>
                ) : (
                  <>
                    <IconSparkle3Outline18 className="h-3 w-3" />
                    Pay & Import
                  </>
                )}
              </span>
            </Button>
          </div>
        );

      case "importing":
        return null;

      case "success":
        return (
          <div className="flex items-center justify-end gap-3">
            <Button variant="white" onClick={wizard.handleReset}>
              <span className="text-xs">Import Another</span>
            </Button>
            <Link href="/workflows/edit">
              <Button>
                <span className="flex items-center gap-2 text-xs">
                  Create Workflow
                  <IconArrowRightOutline18 className="h-3 w-3" />
                </span>
              </Button>
            </Link>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <PageLayout header={{ useCommandMenu: false }}>
      <div className="mb-6 flex w-full flex-col items-center">
        <div className="mt-4 flex w-full max-w-xl items-center justify-center gap-3">
          <div>
            <h1 className="text-ds-text-primary text-center text-2xl font-bold tracking-tight">Import x402 Endpoint</h1>
            <p className="body-sm text-center">Add a payment-gated API endpoint to your workflow actions</p>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <X402StepIndicator currentStep={wizard.step} variant="page" />
      </div>

      <div className="mx-auto max-w-2xl">
        <div className="border-ds-border-default bg-ds-surface-card shadow-ds-soft rounded-xl border p-6">
          <h2 className="text-ds-text-primary mb-4 text-lg font-semibold">{wizard.stepTitle}</h2>
          {wizard.content}
          <div className="dark:border-ds-neutral-700 mt-6">{renderFooter()}</div>
        </div>
      </div>
    </PageLayout>
  );
}
